import { useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteBuilding, updateBuilding } from "../../../../api/building";
import { enumbuildingtypeToI18nKey } from "../../../../generated/enums";
import {
  Building,
  DeviceWithoutLegacyPassword,
} from "../../../../generated/types";
import { Button } from "../../../common/Button";
import { sectionToIcon } from "../../../type/default";
import {
  BuildingFields,
  buildingToForm,
  PRODUCTION_OPTIONS,
  toUpdatedBuilding,
  useBuildingForm,
} from "../../building/BuildingFields";
import { SerenityConfigsButton } from "../../device/SerenityConfigsModal";
import { SetupError } from "../../setup/SetupLayout";
import { DeleteEmptyModal } from "../DeleteEmptyModal";
import { BuildingDevicesList } from "./BuildingDevicesList";
import { DeviceLicenses } from "./DeviceLicenses";
import { DeleteButton, DetailCard, EditButton, InfoRow } from "./DetailCard";

export const BuildingCard = ({
  building,
  devices,
  canEdit,
  canDelete,
  onSaved,
  onAssociateDevice,
  licenseCompanyId = 0,
}: {
  building: Building;
  devices: DeviceWithoutLegacyPassword[];
  canEdit: boolean;
  canDelete: boolean;
  onSaved: () => Promise<void>;
  onAssociateDevice?: () => void;
  /** Entreprise des produits, pour gérer leurs licences ; 0 les masque. */
  licenseCompanyId?: number;
}) => {
  const { t } = useTranslation();
  const buildingForm = useBuildingForm(buildingToForm(building));
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const productionLabel = PRODUCTION_OPTIONS.find(
    (option) => option.value === building.production_type
  )?.label;

  const startEditing = () => {
    buildingForm.reset(buildingToForm(building));
    setError("");
    setIsEditing(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!buildingForm.validate()) return;

    setSubmitting(true);
    const result = await updateBuilding(
      toUpdatedBuilding(building, buildingForm.form)
    );
    if (!result.success) {
      setSubmitting(false);
      setError(t("error-building-update-failed"));
      return;
    }
    await onSaved();
    setSubmitting(false);
    setIsEditing(false);
  };

  return (
    <DetailCard
      title={building.name}
      icon={sectionToIcon.section_building}
      action={
        isEditing ? null : (
          <div className="flex">
            {canEdit && <EditButton onClick={startEditing} />}
            {canDelete && (
              <DeleteButton
                label={t("building-delete")}
                onClick={() => setIsDeleteOpen(true)}
              />
            )}
          </div>
        )
      }
    >
      {isEditing ? (
        <form onSubmit={submit}>
          {error && (
            <div className="mb-3">
              <SetupError message={error} />
            </div>
          )}
          <BuildingFields buildingForm={buildingForm} layout="row" />
          <div className="flex justify-end gap-2 pt-3">
            <Button
              size="small"
              type="button"
              variant="secondary"
              onClick={() => setIsEditing(false)}
            >
              {t("button-cancel")}
            </Button>
            <Button type="submit" size="small" disabled={submitting}>
              {submitting ? t("setup-saving") : t("button-save")}
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <dl>
            <InfoRow label={t("building-inuav")} value={building.inuav} />
            <InfoRow
              label={t("building-type")}
              value={
                building.type ? t(enumbuildingtypeToI18nKey[building.type]) : ""
              }
            />
            <InfoRow
              label={t("building-production-type")}
              value={productionLabel ? t(productionLabel) : ""}
            />
          </dl>

          <div>
            <h3 className="text-sm font-oxaniumMedium text-grey-medium mb-2">
              {t("section-regulators")}
            </h3>
            <BuildingDevicesList
              devices={devices}
              renderAction={(device) =>
                device.type === "serenity" && (
                  <SerenityConfigsButton uuid={device.uuid} />
                )
              }
              renderDetails={
                licenseCompanyId
                  ? (device) =>
                      device.type === "serenity" && (
                        <DeviceLicenses
                          device={device}
                          companyId={licenseCompanyId}
                        />
                      )
                  : undefined
              }
            />
            {devices.length === 0 && onAssociateDevice && (
              <Button
                variant="secondary"
                size="small"
                className="mt-2"
                onClick={onAssociateDevice}
              >
                {t("device-link-title")}
              </Button>
            )}
          </div>
        </div>
      )}
      {canDelete && (
        <DeleteEmptyModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          title={t("building-delete")}
          warningText={t("building-delete-warning", { name: building.name })}
          // Les lots ne sont pas connus ici : l'API les signale par un refus.
          blockedText={devices.length > 0 ? t("building-delete-not-empty") : ""}
          onDelete={() => deleteBuilding(building.id)}
          onDeleted={() => {
            setIsDeleteOpen(false);
            onSaved();
          }}
        />
      )}
    </DetailCard>
  );
};
